'use client';

/**
 * Scroll reveal, de izquierda a derecha, como en maisoncatalove.com.
 *
 * La referencia (theme Shopify de maisoncatalove.com) lo hace con un
 * `reveal-animation.js` propio y esta CSS:
 *
 * ```css
 * .reveal-item {
 *   --reveal-stagger: 0.1s;
 *   --reveal-offset: 16px;
 *   opacity: 0;
 *   translate: 0 var(--reveal-offset);
 *   transition-duration: 0.5s, 1s;
 *   transition-timing-function: linear, cubic-bezier(0.215, 0.61, 0.355, 1);
 *   transition-property: opacity, translate;
 *   transition-delay: calc(var(--reveal-order, 0) * var(--reveal-stagger));
 * }
 * .reveal-item.is-in-view { opacity: 1; translate: 0px; }
 * ```
 *
 * Y el script hace dos cosas: un `IntersectionObserver` que agrega
 * `.is-in-view`, y el calculo de `--reveal-order` **por fila**, para que la
 * cascada vaya de izquierda a derecha y no de arriba abajo.
 *
 * ## Lo que NO puede pasar
 *
 * **Sin JavaScript, todo el contenido tiene que ser visible.** Si el CSS
 * oculta el contenido y el JS no corre, los buscadores indexan una pagina
 * vacia y el sitio se ve roto. Por eso:
 *
 * - El `<html>` arranca con `class="no-js"` y un script inline en el `<head>`
 *   lo cambia a `js` **antes del primer paint**.
 * - El CSS del reveal **solo** aplica bajo `html.js`.
 * - `@media (prefers-reduced-motion: reduce)` desactiva todo: el contenido
 *   se ve directo, sin transiciones.
 *
 * El marcado (`data-reveal-group`, `data-reveal-item`) va en el HTML del
 * servidor, asi que no hay flash: el CSS aplica desde el primer paint y el
 * observer revela lo que ya esta en pantalla de inmediato.
 *
 * Cero dependencias: `IntersectionObserver` nativo, CSS puro.
 */

import { useEffect, useRef, type ElementType, type ReactNode } from 'react';

/** Una fila es un conjunto de hijos cuyo `offsetTop` cae dentro de esta ventana. */
const ROW_THRESHOLD = 40;

/** Tope de pasos del stagger: con 12 o mas, la ultima fila tarda demasiado. */
const MAX_STAGGER_STEPS = 12;

type Direccion = 'up' | 'left';

type BaseProps = {
  children: ReactNode;
  /** Etiqueta del elemento. Por defecto `div`. */
  as?: ElementType;
  className?: string;
  /** Estilos inline del elemento (grid, por ejemplo). */
  style?: React.CSSProperties;
  /**
   * Desplazamiento inicial, en px. El manual usa 16px.
   * `up` sube desde abajo; `left` entra desde la izquierda.
   */
  offset?: number;
  /** 'up' (de abajo, como la referencia) o 'left' (de izquierda). */
  direction?: Direccion;
};

/**
 * Revela un bloque suelto: un titulo de seccion, un banner, un parrafo.
 *
 * Es el caso simple: un solo elemento, sin cascada.
 */
export function Reveal({
  children,
  as: Tag = 'div',
  className,
  style,
  offset = 16,
  direction = 'up',
}: BaseProps) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const observer = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (!entrada.isIntersecting) continue;
          entrada.target.classList.add('is-in-view');
          observer.unobserve(entrada.target);
        }
      },
      // Un poco antes de entrar: el elemento ya esta casi listo cuando el
      // usuario lo ve.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [offset, direction]);

  return (
    <Tag
      ref={ref as never}
      className={className}
      data-reveal-item=""
      style={{ ...style, ...estiloDeRevelado(offset, direction) }}
    >
      {children}
    </Tag>
  );
}

/**
 * Agrupa hijos y los revela en cascada **de izquierda a derecha**.
 *
 * Un solo `IntersectionObserver` por grupo (no uno por item): con 23
 * productos, 23 observers es una pena.
 */
export function RevealGroup({
  children,
  as: Tag = 'div',
  className,
  style,
  itemSelector,
  offset = 16,
  direction = 'up',
}: BaseProps & {
  /**
   * Selector de los hijos que se revelan. Por defecto, los hijos directos.
   * Se usa cuando la grilla tiene wrappers intermedios.
   */
  itemSelector?: string;
}) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    // Si el usuario pide menos movimiento, no se anima nada: el CSS ya no
    // oculta el contenido (esta bajo `prefers-reduced-motion: no-preference`).
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const items = Array.from(
      root.querySelectorAll<HTMLElement>(itemSelector ?? ':scope > *'),
    );
    if (!items.length) return;

    /*
     * El orden del stagger se calcula POR FILA: los hijos que comparten la
     * misma linea vertical (dentro de ROW_THRESHOLD px) se ordenan por su
     * `offsetLeft`. Asi el primero de la fila entra primero y el ultimo
     * ultimo: la cascada va de izquierda a derecha.
     *
     * La referencia de Shopify usa un tope de 12 pasos.
     *
     * Solo los hijos con layout tienen fila: uno que todavia esta oculto
     * (un panel de tabs, por ejemplo) tiene `offsetTop` 0 y mezclarlo
     * descolocaria a los demas. Esos se dejan sin `--reveal-order`, asi que
     * entran sin cascada pero **igualmente visibles**: si algo falla, el
     * contenido se ve.
     */
    const conLayout = items.filter(
      (el) => el.offsetParent !== null || el.getClientRects().length > 0,
    );

    const filas: HTMLElement[][] = [];
    for (const item of conLayout) {
      const fila = filas.find(
        (f) => Math.abs(f[0].offsetTop - item.offsetTop) <= ROW_THRESHOLD,
      );
      if (fila) fila.push(item);
      else filas.push([item]);
    }

    for (const fila of filas) {
      fila.sort((a, b) => a.offsetLeft - b.offsetLeft);
      fila.forEach((el, i) => {
        el.style.setProperty('--reveal-order', String(Math.min(i, MAX_STAGGER_STEPS)));
      });
    }

    const observer = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (!entrada.isIntersecting) continue;
          entrada.target.classList.add('is-in-view');
          observer.unobserve(entrada.target);
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 },
    );

    for (const item of items) observer.observe(item);
    return () => observer.disconnect();
  }, [itemSelector, offset, direction]);

  return (
    <Tag
      ref={ref as never}
      className={className}
      data-reveal-group=""
      style={{ ...style, ...estiloDeRevelado(offset, direction) }}
    >
      {children}
    </Tag>
  );
}

/**
 * El desplazamiento inicial, en variables CSS.
 *
 * Se USA `--reveal-x`/`--reveal-y` en vez de dos reglas con `translate`: los
 * hijos heredan las variables del grupo, asi que una sola regla de CSS
 * alcanza para las dos direcciones y no hay pelea de especificidad.
 */
function estiloDeRevelado(offset: number, direction: Direccion) {
  return {
    '--reveal-offset': `${offset}px`,
    '--reveal-x': direction === 'left' ? `-${offset}px` : '0px',
    '--reveal-y': direction === 'left' ? '0px' : `${offset}px`,
  } as React.CSSProperties;
}
